import React, { useState } from 'react';
import { Button, Card, Descriptions, Space, Tag, Tooltip, Typography, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { audienceLabel, countryFlag, istDateTime } from '@/utils/cricketCalendar';
import type { CricketFixture, FixtureStatus } from '@/types/matchPushes';

const { Text, Title } = Typography;

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;
/** The most matches one live event is created with (the add-matches API takes 100 a call). */
const MAX_EVENT_MATCHES = 100;

const STATUS_TEXT: Record<FixtureStatus, string> = {
  upcoming: 'Upcoming',
  live: 'Live now',
  finished: 'Finished',
  cancelled: 'Cancelled',
};

const TRACKED_BY_TEXT: Record<string, string> = {
  auto: 'by the tracking rule',
  admin: 'switched on by an admin',
  off_by_admin: 'switched off by an admin',
};

const PLAN_STATUS_ORDER = [
  'sent',
  'scheduled',
  'waiting',
  'due',
  'sending',
  'paused',
  'skipped',
  'cancelled',
];

/** "1 sent, 4 scheduled" from a plans.byStatus map. */
const planSummary = (byStatus: Record<string, number>): string =>
  Object.entries(byStatus)
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => PLAN_STATUS_ORDER.indexOf(a) - PLAN_STATUS_ORDER.indexOf(b))
    .map(([status, n]) => `${n} ${status}`)
    .join(', ');

interface Props {
  fixture: CricketFixture;
  /** False for show-only admins: the change buttons are disabled with `deniedTitle`. */
  canChange: boolean;
  deniedTitle?: string;
  onEdit: () => void;
  onClose?: () => void;
}

/** Side panel for one match: details, its pushes, the live events using it, and the shortcuts. */
export const FixtureDrawer: React.FC<Props> = ({
  fixture,
  canChange,
  deniedTitle,
  onEdit,
  onClose,
}) => {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const pushes = fixture.plans?.total ?? 0;

  const startLiveEvent = async () => {
    setOpening(true);
    try {
      // Only matches still to come (upcoming or live), at most as many as the add-matches form takes,
      // so the URL stays short; the form adds them to the event.
      const comingUp = (f: CricketFixture) => f.status === 'upcoming' || f.status === 'live';
      let ids = comingUp(fixture) ? [fixture.id] : [];
      if (fixture.seriesSlug) {
        const now = Date.now();
        const response = await adminApi.cricketCalendar.listFixtures({
          series: fixture.seriesSlug,
          from: new Date(now - DAY_MS).toISOString(),
          to: new Date(now + YEAR_MS).toISOString(),
          limit: MAX_EVENT_MATCHES,
        });
        ids = response.data.items.filter(comingUp).map((f) => f.id);
        if (comingUp(fixture) && !ids.includes(fixture.id)) ids.unshift(fixture.id);
        if (response.data.total > response.data.items.length || ids.length > MAX_EVENT_MATCHES) {
          message.info(
            `Only the first ${MAX_EVENT_MATCHES} upcoming matches are added; add the rest from the event.`
          );
        }
        ids = ids.slice(0, MAX_EVENT_MATCHES);
      }
      if (ids.length === 0) {
        message.warning('This match is over or cancelled, and the series has no upcoming matches.');
        return;
      }
      const query = new URLSearchParams({ fixtures: ids.join(',') });
      if (fixture.seriesName) query.set('series', fixture.seriesName);
      navigate(`/live-events/create?${query.toString()}`);
    } catch (error) {
      message.error(apiErrorMessage(error, "Failed to load the series' matches"));
    } finally {
      setOpening(false);
    }
  };

  return (
    <Card
      size="small"
      extra={onClose ? <a onClick={onClose}>Close</a> : undefined}
      title={
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {fixture.team1Name} vs {fixture.team2Name}
          </Title>
          <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
            {[fixture.matchLabel, istDateTime(fixture.startsAt), fixture.venue]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </div>
      }
    >
      <Title level={5} style={{ marginTop: 0, fontSize: 13 }}>
        Match
      </Title>
      <Descriptions size="small" column={1} colon={false} labelStyle={{ width: 90 }}>
        <Descriptions.Item label="Status">{STATUS_TEXT[fixture.status]}</Descriptions.Item>
        <Descriptions.Item label="Tracked">
          {fixture.tracked ? 'Yes' : 'No'} (
          {TRACKED_BY_TEXT[fixture.trackedBy] ?? fixture.trackedBy})
        </Descriptions.Item>
        <Descriptions.Item label="Plays to">
          {fixture.audienceCountries.length
            ? `${fixture.audienceCountries.map(countryFlag).join(' ')} ${audienceLabel(fixture.audienceCountries)}`
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Source">
          {fixture.source === 'manual' ? (
            'Added by hand'
          ) : (
            <span>
              Sportskeeda <Text type="secondary">· {fixture.sourceId}</Text>
            </span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Result">
          {fixture.resultText ? (
            fixture.resultText
          ) : (
            <Text type="secondary">fills in after the match</Text>
          )}
        </Descriptions.Item>
        {fixture.lockedFields.length > 0 && (
          <Descriptions.Item label="Edited by hand">
            {fixture.lockedFields.map((f) => (
              <Tag key={f}>{f}</Tag>
            ))}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Title level={5} style={{ fontSize: 13 }}>
        Pushes for this match
      </Title>
      {pushes > 0 ? (
        <div>
          <Text>
            {pushes} plan{pushes === 1 ? '' : 's'}
          </Text>{' '}
          <Text type="secondary">{planSummary(fixture.plans.byStatus)}</Text>
        </div>
      ) : (
        <Text type="secondary">
          {fixture.tracked ? 'None planned yet' : 'Not tracked, so none'}
        </Text>
      )}
      <div style={{ marginTop: 4 }}>
        <Link to={`/match-pushes?fixture=${fixture.id}`}>Open in Upcoming queue →</Link>
      </div>

      <Title level={5} style={{ fontSize: 13 }}>
        Live events using this match
      </Title>
      {fixture.liveEvents?.length ? (
        fixture.liveEvents.map((event) => (
          <div key={event.id}>
            <Link to={`/live-events/${event.id}`}>{event.name}</Link>
          </div>
        ))
      ) : (
        <Text type="secondary">None</Text>
      )}

      <Space wrap style={{ marginTop: 14 }}>
        <Tooltip title={deniedTitle}>
          <Button
            type="primary"
            size="small"
            loading={opening}
            disabled={!canChange}
            onClick={startLiveEvent}
          >
            + Live event for this series
          </Button>
        </Tooltip>
        <Tooltip title={deniedTitle}>
          <Button size="small" disabled={!canChange} onClick={onEdit}>
            Edit match
          </Button>
        </Tooltip>
      </Space>
    </Card>
  );
};
